// stores/collectorDayStore.js
//
// The collector's day (collector-app P1-C; kept on the phone in P2-B). Holds
// the plans I collect on today, the one I am looking at, and that plan's day
// document exactly as the backend built it (`collector-plans` show — brief
// 2026-09-30-collector-day-read): `plan`, `cash`, `progress`, `shops[]` with
// their `lines[]`.
//
// The app never derives a shop's «خلّصت» or any other state the backend sends:
// every screen reads this document. `refresh()` re-reads it with the stored
// ETag, so an unchanged day costs one empty 304.
//
// `day` — what the screens render — is `serverDay` with the action layer's
// overlay re-applied on top (collector/dayModel.js `rebaseDay`): taps still
// queued or on the wire, and taps answered but not yet in a read — and, last,
// the collector's own photos the server does not have yet, from their Blobs
// (collector/photoQueue.js `localPhotos`), and the shop invoices filed on the
// phone and not sent (collector/invoiceQueue.js `queued` — a flag on the shop,
// never its state). A read
// prunes from the overlay whatever of ITS plan was answered before it started.
//
// ON THE PHONE (P2-B, L-E and L-I; collector/db.js `day`): before any read,
// the action layer's `ensureHydrated()` puts the stored document of the
// running plan in `serverDay` (never over one a read already brought). Every
// read the server answers is stored — the document, its ETag and `fetchedAt`
// — in ONE transaction with the taps it now contains. A 304 rewrites the
// record only when the stored `fetchedAt` is over a minute old. Switching
// plans with no signal shows that plan's stored document.
//
// CLEARED ONLY AT PLAN CLOSE (L-I): a read that says the plan is completed or
// cancelled deletes its stored day, answered taps and uploaded photos — only
// when nothing of that plan still waits (checked in the same transaction as
// the delete); otherwise it is stored, and cleaned by the read after the
// queue drains. A stored day whose plan left today's list (it closed after
// the collector's last visit) gets ONE read per session to learn that; a
// 403 or 404 keeps it. Conflicts stay until dismissed; the image cache is
// left to its own expiry.
//
// EVERY PLAN OF TODAY, not only the one he opened (collector-offline P3-A): the
// cold first read (the full index) stores every open plan it carries; a warm
// one reads each open plan the phone does not hold yet, ONCE per session
// (`keepOtherPlans`). A plan that is not the active one is only STORED: it
// never announces answers, prunes the overlay or touches `serverDay`, `etag`
// and `lastSyncedAt` — those belong to the plan on screen. Its record carries
// `unseen: true` until it is first shown (`showStored`): her answers that
// arrived meanwhile are told then, against an empty baseline, and the flag
// goes. Stored from an index it has no ETag; from its own read it has one.
//
// Every document accepted also starts the MORNING DOWNLOAD of its pictures
// (stores/collectorPrewarmStore.js, P2-C) — fire and forget. It is handed the
// pictures of every open plan on the phone (P3-A part B, `prewarmDocument`).
import { defineStore } from 'pinia';
import { computed, ref, shallowRef, toRaw } from 'vue';
import { useCollectorActionsStore } from '@/collector/actions.js';
import { newlyAnswered, rebaseDay } from '@/collector/dayModel.js';
import { collectorStorage, requestPersistentStorage } from '@/collector/db.js';
import { useCollectorInvoicesStore } from '@/collector/invoiceQueue.js';
import { collectorNetwork } from '@/collector/network.js';
import { useCollectorPhotosStore } from '@/collector/photoQueue.js';
import { PREWARM_URL_BUDGET, unionPrewarmDay } from '@/collector/prewarm.js';
import { authPaused, collectorUserId, pauseForAuth, sessionAlive } from '@/collector/session.js';
import { fetchCollectorPlan, fetchCollectorPlans } from '@/services/collectorDay.service.js';
import { useCollectorPrewarmStore } from '@/stores/collectorPrewarmStore.js';

/** The route param for the backend's one shop with `supplierId: null`. */
export const NO_SHOP_KEY = 'none';

/** A 304 refreshes the stored `fetchedAt` at most this often (the record is ~100 KB). */
const NOT_MODIFIED_WRITE_MS = 60_000;

/** A plan in one of these is over: its day leaves the phone once nothing waits (L-I). */
const CLOSED_PLAN = new Set(['completed', 'cancelled']);

function isClosed(doc) {
  return CLOSED_PLAN.has(doc?.plan?.status);
}

/** When a stored record was fetched, in ms (0 when it says nothing). */
function fetchedMs(record) {
  return Date.parse(record?.fetchedAt) || 0;
}

/**
 * The day page's «العضوة ردّت على …» card for one answered question (L-G): a
 * snapshot of what the collector needs to read and to open the shop — the
 * server's own `instruction`, never rebuilt. One id per (line, `respondedAt`).
 */
function answerCard({ shop, line }, doc, syncedAt) {
  const question = line.activeQuestion;
  return {
    id: `answered:${line.id}:${question.respondedAt ?? 'answered'}`,
    planId: doc?.id != null ? String(doc.id) : null,
    state: 'answered',
    reason: null,
    type: null,
    planItemId: String(line.id),
    productName: line.productName ?? null,
    memberCode: line.memberCode ?? null,
    shopName: shop?.name ?? null,
    shopKey: shop ? shopKey(shop) : null,
    response: question.response ?? null,
    instruction: question.instruction ?? null,
    respondedAt: question.respondedAt ?? null,
    syncedAt,
    seenAt: syncedAt,
  };
}

/** A shop's key in URLs: its supplier id, or `none` for the no-shop group. */
export function shopKey(shop) {
  return shop?.supplierId == null ? NO_SHOP_KEY : String(shop.supplierId);
}

/** The plan to open when nothing is chosen: the running one, else the next. */
function pickDefaultPlan(rows) {
  const byStatus = (status) => rows.find((row) => row?.plan?.status === status);
  return (byStatus('in_progress') ?? byStatus('confirmed') ?? rows[0])?.id ?? null;
}

export const useCollectorDayStore = defineStore('collectorDay', () => {
  /** Light rows from the index (`fields=plan,progress`): one per plan today. */
  const plans = ref([]);
  const activePlanId = ref(null);
  /** The last document the server sent for `activePlanId` — replaced whole, never patched. */
  const serverDay = shallowRef(null);
  const etag = ref(null);
  /** Client time of the last read the server answered (200 or 304) — `fetchedAt` on the phone. */
  const lastSyncedAt = ref(null);
  /** 'idle' | 'loading' | 'ready' | 'empty' | 'error' */
  const status = ref('idle');
  const error = ref(null);

  /** The days kept on the phone, by plan id: `{ planId, userId, doc, etag, fetchedAt }`. */
  const storedDays = new Map();

  /** The day the screens render: the server's document + the taps it has not caught up with. */
  const day = computed(() =>
    rebaseDay(
      serverDay.value,
      useCollectorActionsStore().overlay,
      useCollectorPhotosStore().localPhotos,
      useCollectorInvoicesStore().queued,
    ),
  );

  /** Latest change on the plan or my lines (backend `plan.lastUpdatedAt`). */
  const lastUpdatedAt = computed(() => day.value?.plan?.lastUpdatedAt ?? null);

  const shops = computed(() => day.value?.shops ?? []);

  /** A shop of the day by its URL key (`shopKey`). */
  function findShop(key) {
    return shops.value.find((shop) => shopKey(shop) === String(key)) ?? null;
  }

  // ── The phone ──────────────────────────────────────────────────────────────

  /**
   * The stored days of this user (the action layer's hydration). Fills the
   * screen only when no read has landed yet, and sets the document and its
   * ETag together — the ETag always belongs to the document on screen.
   */
  function hydrateDays(records = []) {
    for (const record of records) {
      if (record?.planId == null || !record.doc) continue;
      const key = String(record.planId);
      // The phone's answer can land late: never put an older copy over one a
      // read of this session already stored.
      if (storedDays.has(key) && fetchedMs(storedDays.get(key)) >= fetchedMs(record)) continue;
      storedDays.set(key, record);
    }
    if (serverDay.value || storedDays.size === 0) return;

    const docs = [...storedDays.values()].map((record) => record.doc);
    if (plans.value.length === 0) {
      plans.value = docs.map(({ id, plan, progress }) => ({ id: String(id), plan, progress }));
    }
    const chosen =
      activePlanId.value != null && storedDays.has(activePlanId.value) ? activePlanId.value : pickDefaultPlan(docs);
    if (chosen == null) return;
    activePlanId.value = String(chosen);
    showStored(storedDays.get(String(chosen)));
  }

  function showStored(record) {
    if (!record?.doc) return false;
    serverDay.value = record.doc;
    etag.value = record.etag ?? null;
    lastSyncedAt.value = record.fetchedAt ?? null;
    status.value = 'ready';
    if (record.unseen) markSeen(record);
    return true;
  }

  /**
   * A day kept while another plan was on screen is on screen now: tell what she
   * answered in it (every answer waiting — nobody saw this document before),
   * and keep on the phone that this was done, so a restart never tells it twice.
   */
  function markSeen(record) {
    const seen = { ...record };
    delete seen.unseen;
    storedDays.set(String(seen.planId), seen);
    collectorStorage().acceptDay(seen, []).catch(logStoreError);
    announceAgainst(null, record.doc, new Date().toISOString());
  }

  /**
   * Store what a read brought, with the ids of the taps it now contains
   * (marked `inRead` in the same transaction). Not awaited by the screens.
   */
  function persistRead(doc, { notModified, fetchedAt, caughtUp }) {
    const planId = doc?.id != null ? String(doc.id) : activePlanId.value;
    if (!doc || planId == null) {
      if (caughtUp.length) collectorStorage().acceptDay(null, caughtUp).catch(logStoreError);
      return;
    }
    const previous = storedDays.get(planId);
    if (notModified && previous && Date.parse(fetchedAt) - Date.parse(previous.fetchedAt) < NOT_MODIFIED_WRITE_MS) {
      if (caughtUp.length) collectorStorage().acceptDay(null, caughtUp).catch(logStoreError);
      return;
    }
    const record = { planId, userId: collectorUserId(), doc: toRaw(doc), etag: etag.value, fetchedAt };
    if (isClosed(doc) && !waitingInMemory(planId)) {
      closePlan(planId, record, caughtUp);
      return;
    }
    storedDays.set(planId, record);
    collectorStorage().acceptDay(record, caughtUp).catch(logStoreError);
  }

  /** Taps of this plan in memory not answered yet (the storage checks its own rows). */
  function waitingInMemory(planId) {
    const actions = useCollectorActionsStore();
    return [...actions.queue, ...actions.inflight].some((action) => action.planId == null || String(action.planId) === planId);
  }

  /** L-I: the plan is over — clear it, or store it when something of it still waits. */
  async function closePlan(planId, record, caughtUp = []) {
    try {
      const cleared = await collectorStorage().clearPlan(planId);
      if (cleared) {
        storedDays.delete(planId);
        return;
      }
      if (record) {
        storedDays.set(planId, record);
        await collectorStorage().acceptDay(record, caughtUp);
      }
    } catch (err) {
      logStoreError(err);
    }
  }

  /**
   * A stored day whose plan is not in today's list: it probably closed after
   * the last visit. ONE read per plan per session tells; completed or
   * cancelled → cleared (when nothing waits). A 403, a 404 or no signal keep it.
   */
  const swept = new Set();
  async function sweepStoredDays(rows) {
    const listed = new Set(rows.map((row) => String(row.id)));
    for (const planId of [...storedDays.keys()]) {
      if (listed.has(planId) || swept.has(planId) || planId === activePlanId.value) continue;
      swept.add(planId);
      try {
        const result = await fetchCollectorPlan(planId);
        if (isClosed(result?.day) && !waitingInMemory(planId)) await closePlan(planId, null);
      } catch {
        /* kept: not ours to judge without an answer */
      }
    }
  }

  // ── The plans he did not open (collector-offline P3-A) ─────────────────────

  /** Plans the show refused (403/404) this session: not asked again. Anything else is tried at the next load. */
  const refusedOthers = new Set();
  /** Plans whose own read is on the wire — a second load does not send a second one. */
  const keeping = new Set();

  /**
   * Keep on the phone the day of a plan that is NOT the one on screen. Only
   * stores: no announcement, no pruning, nothing of `serverDay`, `etag` or
   * `lastSyncedAt` (those belong to the active plan). An open plan only, and
   * never over a day the phone already holds. `etag` is null for a day that
   * came with an index (there is no tag for it) and the show's tag otherwise.
   */
  function keepOtherPlan(doc, nextEtag) {
    const planId = doc?.id != null ? String(doc.id) : null;
    if (planId == null || planId === activePlanId.value || isClosed(doc) || storedDays.has(planId)) return;
    const record = {
      planId,
      userId: collectorUserId(),
      doc: toRaw(doc),
      etag: nextEtag ?? null,
      fetchedAt: new Date().toISOString(),
      unseen: true,
    };
    storedDays.set(planId, record);
    collectorStorage().acceptDay(record, []).catch(logStoreError);
    // A plan kept after the day on screen was accepted joins the download of
    // its pictures; while no day is on screen yet, its acceptance will bring it.
    if (serverDay.value) useCollectorPrewarmStore().run(prewarmDocument(serverDay.value));
  }

  /** One read of a listed plan the phone does not hold. Never throws. */
  async function readOtherPlan(planId) {
    keeping.add(planId);
    try {
      const result = await fetchCollectorPlan(planId);
      if (result?.day) keepOtherPlan(result.day, result.etag);
    } catch (err) {
      const code = err?.response?.status;
      if (code === 403 || code === 404) refusedOthers.add(planId);
      // no signal, a 5xx, a page in the way: the next load tries again
    } finally {
      keeping.delete(planId);
    }
  }

  /**
   * After the list of today's plans: keep the others. A full index already
   * carries every day — store the open ones, no extra request. The light one
   * does not — each open plan the phone has no day of gets ONE read of its own.
   * A stored plan the list now says is over leaves the phone (L-I, when nothing
   * of it waits), as the active one does. Fire and forget.
   */
  async function keepOtherPlans(rows, { full }) {
    for (const row of rows) {
      const planId = String(row.id);
      if (planId === activePlanId.value) continue;
      if (isClosed(row)) {
        if (storedDays.has(planId) && !waitingInMemory(planId)) closePlan(planId, null);
      } else if (full) {
        if (row.plan) keepOtherPlan(row, null);
      } else if (!storedDays.has(planId) && !keeping.has(planId) && !refusedOthers.has(planId)) {
        await readOtherPlan(planId);
      }
    }
  }

  function logStoreError(err) {
    console.error('[collectorDay] storing the day on the phone failed', err);
  }

  // ── Reads ──────────────────────────────────────────────────────────────────

  /**
   * Reload the list of today's plans; keep the chosen one if it is still there.
   * `full` reads every attribute (the whole day of each plan) instead of the
   * light `plan,progress` fieldset — the cold first paint uses it so the day
   * shows after ONE round trip instead of index-then-show.
   */
  async function loadPlans({ full = false } = {}) {
    const rows = await fetchCollectorPlans(full ? {} : { fields: 'plan,progress' });
    plans.value = rows.map(({ id, plan, progress }) => ({ id, plan, progress }));
    if (!rows.some((row) => row.id === activePlanId.value)) {
      setActivePlan(pickDefaultPlan(rows));
    }
    sweepStoredDays(rows);
    keepOtherPlans(rows, { full }).catch(logStoreError);
    return rows;
  }

  /** Where the action layer's answers stood when a read went out. */
  function readMark() {
    return useCollectorActionsStore().epoch;
  }

  /**
   * Take a document the server just sent as the day. `mark` = readMark()
   * taken BEFORE the request: every tap of this plan answered by then is in
   * this document and leaves the overlay; anything answered later stays until
   * the next read.
   */
  function acceptDay(doc, nextEtag, mark, { notModified = false } = {}) {
    const planId = doc?.id != null ? String(doc.id) : activePlanId.value;
    const caughtUp = mark != null ? useCollectorActionsStore().pruneThrough(mark, planId) : [];
    const fetchedAt = new Date().toISOString();
    announceNewAnswers(doc, fetchedAt);
    serverDay.value = doc;
    etag.value = nextEtag;
    lastSyncedAt.value = fetchedAt;
    error.value = null;
    status.value = doc ? 'ready' : 'empty';
    persistRead(doc, { notModified, fetchedAt, caughtUp });
    // The first day on this phone: ask the browser to keep the collector's
    // storage through disk pressure (L-I). Once per session, never awaited.
    if (doc) requestPersistentStorage();
    // The morning download (P2-C): the day is on the phone, so fetch its
    // pictures for the offline hours. A 304 only checks the cache. Never awaited.
    // With no day (a leader) it still refreshes the shops picked on the phone
    // (supplier-preview-offline L-H).
    useCollectorPrewarmStore().run(doc ? prewarmDocument(doc) : null);
  }

  /**
   * What the morning download is handed (P3-A part B): the plan on screen first,
   * then every other open plan kept on the phone, as ONE document — so
   * «يومك جاهز من غير نت» is said only when the pictures of all of them are in
   * the cache. With no other plan it is `active` itself, untouched.
   */
  let cutSaid = false;
  function prewarmDocument(active) {
    const activeId = active?.id != null ? String(active.id) : null;
    const others = [...storedDays.values()]
      .filter((record) => String(record.planId) !== activeId && !isClosed(record.doc))
      .map((record) => record.doc);
    const { day: handed, trimmed } = unionPrewarmDay(active, others);
    if (trimmed && !cutSaid) {
      cutSaid = true;
      console.warn(
        `[collectorDay] over ${PREWARM_URL_BUDGET} pictures for the day: the other plans keep their thumbnails, not their medium size`,
      );
    }
    return handed;
  }

  /**
   * A read brought a question she has newly answered (L-G): ONE card on the day
   * page, no sound, no push. Against the document held BEFORE this read — the
   * stored day on a restart, so a reload never says it twice. A day that is
   * over says nothing, and an unchanged day (a 304 hands back the same
   * document) has nothing new.
   */
  function announceNewAnswers(doc, syncedAt) {
    const held = serverDay.value;
    if (!doc || doc === held || isClosed(doc)) return;
    const before = held && String(held.id) === String(doc.id) ? held : null;
    announceAgainst(before, doc, syncedAt);
  }

  /** The cards for what `doc` has answered that `before` (null = nothing seen) did not. A day that is over says nothing. */
  function announceAgainst(before, doc, syncedAt) {
    if (!doc || isClosed(doc)) return;
    const fresh = newlyAnswered(before, doc);
    if (fresh.length) useCollectorActionsStore().announceAnswers(fresh.map((entry) => answerCard(entry, doc, syncedAt)));
  }

  /** Choose a plan; with its stored document on screen until a read replaces it. */
  function setActivePlan(planId) {
    const next = planId == null ? null : String(planId);
    if (next === activePlanId.value) return;
    activePlanId.value = next;
    serverDay.value = null;
    etag.value = null;
    if (next != null) showStored(storedDays.get(next));
  }

  // One read at a time. A refresh asked for while one is running is not
  // dropped: the running one finishes, then ONE more read goes out, so the
  // screen never settles on a document fetched before the change that asked
  // for the refresh (a flush answered while a read was already on the wire).
  let inFlight = null;
  let again = false;
  // A plan the show refused (403/404) once. Refused twice in a row means the
  // index and the show disagree — stop instead of looping.
  let refusedPlanId = null;

  /**
   * Re-read the active plan (conditionally, with the ETag). Loads the plan
   * list first when no plan is chosen yet. Never throws: a failed read keeps
   * the last day on screen and leaves the reason in `error`.
   */
  function refresh() {
    if (inFlight) {
      again = true;
      return inFlight;
    }
    inFlight = (async () => {
      try {
        await useCollectorActionsStore().ensureHydrated();
        do {
          again = false;
          await readOnce();
        } while (again);
      } finally {
        inFlight = null;
      }
    })();
    return inFlight;
  }

  /**
   * The day page's entry: the stored day first, then today's plans, then the
   * chosen plan's day. Cold (no day at all) it reads the full index and paints
   * from it — one round trip to a usable screen; the next refresh switches to
   * the ETag'd show. Warm it reloads the light list and revalidates the day
   * (usually a 304). With no signal the stored day stays on screen.
   */
  async function load() {
    await useCollectorActionsStore().ensureHydrated();
    const cold = !serverDay.value;
    const mark = readMark();
    let rows;
    try {
      rows = await loadPlans({ full: cold });
    } catch (err) {
      collectorNetwork().report(err?.response?.status != null);
      error.value = err;
      if (!serverDay.value) status.value = 'error';
      return;
    }
    if (!activePlanId.value) {
      acceptDay(null, null);
      return;
    }
    const fromIndex = cold ? rows.find((row) => row.id === activePlanId.value) : null;
    if (fromIndex?.plan) {
      acceptDay(fromIndex, null, mark);
      return;
    }
    await refresh();
  }

  /** Switch to another plan of today (a second batch) and read it. */
  function selectPlan(planId) {
    setActivePlan(planId);
    return refresh();
  }

  async function readOnce() {
    if (!activePlanId.value) {
      try {
        await loadPlans();
      } catch (err) {
        error.value = err;
        status.value = serverDay.value ? 'ready' : 'error';
        return;
      }
      if (!activePlanId.value) {
        serverDay.value = null;
        status.value = 'empty';
        return;
      }
    }

    const planId = activePlanId.value;
    if (!serverDay.value) status.value = 'loading';
    const mark = readMark();

    try {
      const result = await fetchCollectorPlan(planId, { etag: serverDay.value ? etag.value : null });
      if (planId !== activePlanId.value) return; // switched plans meanwhile — the next read is queued

      collectorNetwork().report(true);
      refusedPlanId = null;
      acceptDay(result.notModified ? serverDay.value : result.day, result.etag, mark, {
        notModified: result.notModified,
      });
      // An authenticated read answered: a queue paused on the login resumes (L-D).
      if (authPaused.value) sessionAlive();
    } catch (err) {
      const code = err?.response?.status;
      // "Online" means our server answers: a read with no answer is evidence too.
      collectorNetwork().report(code != null);
      // The login expired (L-D): the shared 401 handler holds the collector
      // on this day; the queue waits for the login.
      if (code === 401 || code === 419) pauseForAuth();
      // The plan left my day (reassigned, cancelled, or no longer mine): drop
      // it and fall back to whatever plan today still has.
      if ((code === 403 || code === 404) && planId === activePlanId.value && planId !== refusedPlanId) {
        refusedPlanId = planId;
        setActivePlan(null);
        again = true;
        return;
      }
      error.value = err;
      status.value = serverDay.value ? 'ready' : 'error';
    }
  }

  /** Forget everything in memory (another account on this phone) — the phone's storage stays. */
  function clear() {
    plans.value = [];
    activePlanId.value = null;
    serverDay.value = null;
    etag.value = null;
    lastSyncedAt.value = null;
    status.value = 'idle';
    error.value = null;
    storedDays.clear();
    refusedOthers.clear();
    keeping.clear();
    cutSaid = false;
    useCollectorPrewarmStore().reset();
  }

  return {
    plans,
    activePlanId,
    serverDay,
    etag,
    lastSyncedAt,
    status,
    error,
    day,
    lastUpdatedAt,
    shops,
    findShop,
    hydrateDays,
    loadPlans,
    load,
    refresh,
    selectPlan,
    clear,
  };
});
